import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_AVAILABLE_LANGUAGES, ENVIRONMENT, TokenService, UserService } from '@cfp/core';
import { LoginCard, LoginLayout } from '@cfp/shared/ui/login';
import { LoadingSpinner } from '@cfp/ui/common';
import { AlertService } from '@cfp/ui/feedback';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PortfolioItemDTO, PortfoliosService } from 'csr-asset-app';
import { LoginExternalService, OTPTokenDTO, OTPValidateDTO } from 'csr-login-app';
import { FooterText } from '../../components';

@Component({
  selector: 'cfp-otp-authentication-page',
  templateUrl: './otp-authentication-page.html',
  imports: [LoginLayout, LoginCard, TranslateModule, LoadingSpinner, FooterText],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OtpAuthenticationPage implements OnInit {
  protected readonly isLoading = signal(true);
  protected readonly errorMessage = signal<string | undefined>(undefined);

  private readonly environment = inject(ENVIRONMENT);
  private readonly availebaleLanguages = inject(APP_AVAILABLE_LANGUAGES);
  private readonly loginExternalService = inject(LoginExternalService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly tokenService = inject(TokenService);
  private readonly userService = inject(UserService);
  private readonly portfoliosService = inject(PortfoliosService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);

  public ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params.otp) {
        this.otpLogin(params.otp);
      }
    });
  }

  protected closeWindow(): void {
    if (window.opener) {
      window.close();
    } else {
      this.router.navigateByUrl(this.environment.loginRoute);
    }
  }

  protected otpLogin(otp: string): void {
    const otpData: OTPValidateDTO = {
      otp: otp,
      environmentCode: this.environment.environmentCode.dto
    };

    this.loginExternalService.otpValidatePost(otpData).subscribe({
      next: (res: OTPTokenDTO) => {
        // todo: needs tobe refactored to accessToken only there are some backend issues why we cannot yet change
        this.tokenService.setToken(res['access_token']);
        let forceReload: boolean = false;
        const localeId = res.language;

        if (localeId != null && this.translateService.currentLang !== localeId) {
          const hasSomeAvailableLanguages: boolean = this.availebaleLanguages.some((language: string) => {
            return language === localeId;
          });

          if (hasSomeAvailableLanguages) {
            this.translateService.use(localeId);
            this.translateService.currentLang = localeId;
            forceReload = true;
          }
        }

        this.otpGotoApp(res, forceReload);
      },
      error: () => {
        this.isLoading.set(false);
        this.alertService.showAlert('error', 'otp.error.title', 'otp.error.text');
      }
    });
  }

  protected otpGotoApp(otpToken: OTPTokenDTO, forceReload: boolean): void {
    const portfolioBusinessKey = otpToken.portfolioBusinessKey;
    if (!portfolioBusinessKey) {
      this.isLoading.set(false);
      this.alertService.showAlert('error', 'otp.error.title', 'otp.error.text');
      return;
    }
    this.userService.get(true).subscribe({
      next: () => {
        const goToSpecificBuildingGroup =
          otpToken.externalBuildingGroupId != null && otpToken.externalBuildingGroupId.length > 0;

        this.portfoliosService
          .v2PortfoliosByBusinessKeyBusinessKeyGet(portfolioBusinessKey)
          .subscribe((portfolio: PortfolioItemDTO) => {
            this.isLoading.set(false);

            const specificBuildingGroupPath: string = goToSpecificBuildingGroup
              ? `/energy/${portfolio.id}/buildings?externalBuildingGroupId=${otpToken.externalBuildingGroupId}`
              : `/energy/${portfolio.id}`;

            this.router.navigateByUrl(specificBuildingGroupPath).then(() => {
              if (forceReload) {
                window.location.reload();
              }
            });
          });
      },
      error: () => {
        this.alertService.showAlert('error', 'otp.error.title', 'otp.error.text');
        this.isLoading.set(false);
      }
    });
  }
}
